import Head from "next/head";

import { GLOBAL_URL, KR_URL } from "../../../constants/constants";
import { LOCALES_SHORT, type Locale } from "../../../utils/types";

interface HeadMetaProps {
  title?: string;
  description?: string;
  url?: string;
  image?: string;
  locale?: Locale;
  isWebView?: boolean;
}

const DEFAULT_TITLE = "GolfFix";
const DESCRIPTION = "골프픽스와 함께 완전히 새로운 골프 생활";
const DEFAULT_IMAGE = "/images/ai-report/og/image.png";
const SITE_URL = "https://web.golffix.io";

export default function HeadMeta({
  title = DEFAULT_TITLE,
  description = DESCRIPTION,
  url,
  image = DEFAULT_IMAGE,
  locale,
  isWebView,
}: HeadMetaProps): React.ReactElement {
  // URL 계산을 한번만 수행
  const canonicalUrl =
    url || (locale === LOCALES_SHORT.KO ? KR_URL : GLOBAL_URL);
  // og:image는 절대 URL이어야 OG 파서(카카오·페이스북 등)가 인식한다
  const imageUrl = image.startsWith("/") ? `${SITE_URL}${image}` : image;

  return (
    <Head>
      {isWebView ? (
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, viewport-fit=cover, minimum-scale=1.0,maximum-scale=1.0, user-scalable=0"
        />
      ) : (
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, viewport-fit=cover"
        />
      )}
      <meta name="theme-color" content="#363636" />
      <meta name="msapplication-navbutton-color" content="#363636" />

      {/* HTML Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />

      {/* Facebook Meta Tags */}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />

      <meta property="og:locale" content="en_US" />
      <meta property="og:locale:alternate" content="ko_KR" />
      <meta name="application-name" content="GolfFix" />
      <meta name="msapplication-tooltip" content="GolfFix" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:article:author" content="moais" />

      {/* Twitter Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta property="twitter:domain" content="web.golffix.io" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
    </Head>
  );
}
